import { assert, lang } from 'zf-infra';
import styles from './style.module.less';
import { type GridDesignerProps, type GridDesignerData, type CellValue, type CellSetting, type CellPos, type Action, CellType, CellEnable, CellOptional, CellEmpty } from './types';

class GridDesignerService {

  initialValues = {
    title: '',
    empty: CellEmpty.NO,
    placeholder: '',
    type: CellType.NUMBER,
    fx: '',
    ID: '',
    condition: [],
    enable: CellEnable.ENABLED,
    optional: CellOptional.YES,
    copy: false
  };

  /**
   * 获取表格全部数据（合并setting与value的值），不包含标题部分的值
   */
  compose(data: GridDesignerProps): CellSetting[][] {
    const rows = data.gridRow ?? 1;
    const cols = data.gridCol ?? 1;
    const colTitles = data.gridColTitle ?? 0;
    const rowTitles = data.gridRowTitle ?? 0;
    const values = data.value ?? [];
    const options = data.option ?? [];

    // 先做一个空的二维数组填充0，因为支持多个行/列的头，所以取数据时需要跳过行/列标题数
    const cells = Array(rows).fill(0).map(() => Array(cols).fill(0));
    return cells.map((row, rowIndex) => {
      const rowOption = options[rowIndex] ?? [];
      const rowValue = values[rowIndex - rowTitles] ?? [];

      return row.map((col, colIndex) => {
        const colOption = rowOption[colIndex] ?? { title: '' };
        const colValue = rowValue[colIndex - colTitles];
        const isTitle = rowIndex < rowTitles || colIndex < colTitles;
        return {
          ...colOption,
          title: (isTitle ? colOption.title : colValue) ?? '',
          field: `${String.fromCharCode(64 + colIndex + 1)}${rowIndex + 1}`
        };
      });
    });
  }

  /**
   * 显示数据时为了渲染简单，将设置与数值进行了合并，这里需要将其拆分。
   * 即，拆分出 option 与 value 的值
   */
  decompose(values: CellSetting[][], colTitles = 0, rowTitles = 0): GridDesignerData {
    // 提取表格值（忽略行标题和列标题）
    const value = values.reduce<CellValue[][]>((rowMemo, row, rowIndex) => {
      // 忽略行标题
      if (rowIndex >= rowTitles) {
        const cols = row.reduce<CellValue[]>((colMemo, col, colIndex) => {
          // 忽略列标题
          if (colIndex >= colTitles) {
            colMemo.push(col.title ?? '');
          }
          return colMemo;
        }, []);
        rowMemo.push(cols);
      }
      return rowMemo;
    }, []);

    // 获取表格属性
    values.forEach((row, rowIndex) => row.forEach((col: Partial<CellSetting>, colIndex: number) => {
      // 去除空值节省空间
      if (col.title === '') {
        delete col.title;
      }
      if (col.width === '') {
        delete col.width;
      }
      // 去除数据区的值（标题列变更为数据列等情况需要去除）
      if (colIndex >= colTitles && rowIndex >= rowTitles) {
        delete col.title;
      }
    }));

    const result: GridDesignerData = { value, option: values };

    // 有设置数据时
    result.gridRow = values.length;
    result.gridCol = values.length > 0 ? values[0].length : 0;
    return result;
  }

  /**
   * 给表格标题添加颜色
   * 默认表格是白色，而标题需要设置为灰色
   */
  getClassName(rowIndex: number, colIndex: number, rowCount: number, colCount: number): string | undefined {
    if (rowIndex < rowCount || colIndex < colCount) {
      return styles.gridTitle;
    }
  }

  getChangedOption(values: CellSetting[][], cell: CellPos, value: CellSetting): CellSetting[][] {
    const result = lang.clone(values);
    for (const [rowIndex, row] of result.entries()) {
      if (!(rowIndex === cell.row || cell.row < 0)) {
        continue;
      }
      for (const [colIndex, col] of row.entries()) {
        if (!(colIndex === cell.col || cell.col < 0)) {
          continue;
        }
        Object.assign(col, value);
      }
    }
    return result;
  }

  getChangedValues(values: CellSetting[][], action: Action, cell: CellPos, value?: CellSetting): CellSetting[][] {
    if (action === 'change') {
      assert(value != null, 'value is required');
      return service.updateValue(values, cell, value);
    }

    if (action === 'remove-col') {
      return service.removeCol(values, cell.col);
    }

    if (action === 'add-next-col') {
      return service.addCol(values, cell.col, 'next');
    }

    if (action === 'add-prev-col') {
      return service.addCol(values, cell.col, 'prev');
    }

    if (action === 'remove-row') {
      return service.removeRow(values, cell.row);
    }

    if (action === 'add-next-row') {
      return service.addRow(values, cell.row, 'next');
    }

    if (action === 'add-prev-row') {
      return service.addRow(values, cell.row, 'prev');
    }

    assert(false, 'action is invalid');
  }

  private updateValue(values: CellSetting[][], cell: CellPos, value: CellSetting): CellSetting[][] {
    const result = lang.clone(values);
    result[cell.row][cell.col].title = value.title;
    return result;
  }

  /**
   * 在表格中，添加行（拷贝当前行）
   * 当通过修改表格属性的行数时，diff 为变化的行数
   */
  private addRow(data: CellSetting[][], index: number, type: 'prev' | 'next', diff = 1): CellSetting[][] {
    const result = lang.clone(data);

    // 通过 clone 当前行，生成新的 diff 个行, 不 clone 之前的数据
    const rows = Array(diff).fill(0).map(() => data[index].map(() => ({ ...this.initialValues, title: '', field: '' })));

    result.splice(type === 'next' ? index + 1 : index, 0, ...rows as CellSetting[][]);

    return this.resetField(result);
  }

  private removeRow(values: CellSetting[][], index: number, diff = 1): CellSetting[][] {
    const result = lang.clone(values);
    result.splice(index, diff);
    return this.resetField(result);
  }

  /**
   * 遍历整个表格，为每行添加空列
   */
  private addCol(values: CellSetting[][], index: number, type: 'prev' | 'next', diff = 1): CellSetting[][] {
    const result = lang.clone(values);

    result.forEach(row => {
      Array(diff).fill(0)
        .map(() => ({
          ...this.initialValues,
          title: '',
          field: ''
        }))
        .forEach(item => {
          row.splice(type === 'next' ? index + 1 : index, 0, item);
        });
    });

    return this.resetField(result);
  }

  private removeCol(values: CellSetting[][], index: number, diff = 1): CellSetting[][] {
    const result = lang.clone(values);
    result.forEach(row => row.splice(index, diff));
    return this.resetField(result);
  }

  private resetField(values: CellSetting[][]): CellSetting[][] {
    return values.map((row, rowIndex) => row.map((col, colIndex) => ({ ...col, field: `${String.fromCharCode(64 + colIndex + 1)}${rowIndex + 1}` })));
  }

}

export const service = new GridDesignerService();
